import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ReactComponent as ViewIcon } from "../../../../assets/images/icons/view.svg";
import colors from "../../../../theme/color/ColorTheme";
import fontSize from "../../../../theme/fonts/FontTheme";

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  background: #fff;
  border-radius: 8px;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
`;
const TableHead = styled.thead`
  background-color: ${colors.backgroundTintSecondary};
`;
const Th = styled.th`
  padding: 16px;
  font-weight: 500;
  font-size: ${fontSize.s};
  color: #727272;

  &:first-child {
    width: 300px;
  }
`;
const TableBody = styled.tbody``;

const Td = styled.td`
  padding: 10px 16px;
  font-size: ${fontSize.s};
  border-bottom: 1px solid ${colors.dash};
  vertical-align: middle;
  &:first-child {
    width: 300px;
  }
`;

const ProductCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  img {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 4px;
  }

  span {
    display: block;
    font-size: 14px;
    color: #333;
    font-weight: 500;
  }
`;

const ActionIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  background: transparent;
  color: #555;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;

  background: #ddeaf8;
  color: #004181;
  border: 1.5px solid #004181;

  &:hover {
    background: #aacbec;
    color: #004181;
    border: 1.5px solid #004181;
  }
`;
const TableFooter = styled.tfoot``;
const FooterWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const ArrowButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d6d6d6;
  background: transparent;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background 0.2s;

  &:hover {
    background: #f0f0f0;
  }

  svg {
    width: 20px;
    height: 20px;
    stroke: #555;
  }
`;
const Numbers = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;
const PaginationButton = styled.button`
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0 12px;
  border-radius: 50%;
  transition: all 0.2s ease;
  font-weight: 600;
  font-size: ${fontSize.s};

  &:hover {
    background: #f0f0f0;
  }

  &.active {
    background: #004181;
    color: #fff;
    border-color: #004181;
  }
`;

const ProductTable = ({ products, updatePage, page }) => {
  const navigate = useNavigate();

  const getPageNumbers = () => {
    const pages = [];
    if (page > 1) pages.push(page - 1);
    pages.push(page);
    pages.push(page + 1);
    return pages;
  };
  return (
    <TableWrapper>
      <StyledTable>
        <TableHead>
          <tr>
            <Th>Product</Th>
            <Th>Category</Th>
            <Th>Product ID</Th>
            <Th>Colors</Th>
            <Th>Price</Th>
            <Th>Discount</Th>
            <Th>Sold</Th>
            <Th>Actions</Th>
          </tr>
        </TableHead>
        <TableBody>
          {products.map((product) => (
            <tr key={product._id}>
              <Td>
                <ProductCell>
                  <img
                    src={
                      process.env.REACT_APP_IMAGE_BASE_URL +
                      product.images[0]?.path
                    }
                    alt={product.name}
                  />
                  <span>{product.name}</span>
                </ProductCell>
              </Td>
              <Td>{product.category?.name}</Td>
              <Td>{product._id}</Td>
              <Td>{product.colors.length}</Td>
              <Td>{product.price}</Td>
              <Td>{product.discount}</Td>
              <Td>{product.sold}</Td>
              <Td>
                <ActionIcons
                  onClick={() => {
                    navigate(`/admin/products/${product._id}`);
                  }}
                >
                  <ViewIcon />
                  View
                </ActionIcons>
              </Td>
            </tr>
          ))}
          {products && products.length < 1 && (
            <tr>
              <div style={{ textAlign: "center", padding: "40px 0px" }}>
                No records found
              </div>
            </tr>
          )}
        </TableBody>
        <TableFooter>
          <tr>
            <Td colSpan={3} style={{ border: "none" }}>
              <FooterWrapper>
                <div>
                  Showing {products.length} entries (Page {page})
                </div>
                <Pagination>
                  {/* Prev button */}
                  <ArrowButton
                    disabled={page === 1}
                    onClick={() => updatePage((p) => Math.max(p - 1, 1))}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 18L9 12L15 6"
                        stroke="#555"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </ArrowButton>

                  <Numbers>
                    {getPageNumbers().map((p) => (
                      <PaginationButton
                        key={p}
                        className={page === p ? "active" : ""}
                        onClick={() => updatePage(p)}
                        disabled={products.length < 10 && p > page}
                      >
                        {p}
                      </PaginationButton>
                    ))}
                  </Numbers>

                  <ArrowButton
                    disabled={products.length < 10}
                    onClick={() => {
                      updatePage(page + 1);
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 18L15 12L9 6"
                        stroke="#555"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </ArrowButton>
                </Pagination>
              </FooterWrapper>
            </Td>
          </tr>
        </TableFooter>
      </StyledTable>
    </TableWrapper>
  );
};

export default ProductTable;
